import React, {useCallback, useEffect, useMemo, useRef} from "react";
import {css, Theme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {backgroundColor, borderRadius, calculateRemSize, display, flex, flexDirection, mb, mt, mv, onDesktop, p, w100} from "@pg-design/helpers-css";
import {InfoIcon} from "@pg-design/icons-module";
import {UnitsType} from "@pg-design/range-module";
import {Highlight, Text} from "@pg-design/text-module";
import {useTimeout} from "@pg-mono/hooks";
import {rpAppLink} from "@pg-mono/rp-routes";

import {ApplicationModalButton} from "../../../application/components/ApplicationModalButton";
import {ApplicationFormVariant} from "../../../application/types/form/ApplicationFormVariant";
import {GTMEventApplicationSourceSection} from "../../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {IconWithElement} from "../../../credit_calculator/components/IconWithElement";
import {InstallmentBox} from "../../../credit_calculator/components/instalment_calculator/components/InstallmentBox";
import {RangeWithInput} from "../../../credit_calculator/components/RangeWithInput";
import {DEFAULT_INTEREST} from "../../../credit_calculator/constants/calculations_defaults";
import {useCalculatorState} from "../../../credit_calculator/hooks/use_calculator_state";
import {CreditValuesEvent} from "../../../credit_calculator/tracking/analytics/credit_values_event";
import {
    AllCreditGtmEventActions,
    creditCalculatorAnalytics,
    CreditCalculatorGTMEvent,
    CreditCalculatorInstalmentEventAction,
    CreditCalculatorViewActions
} from "../../../credit_calculator/tracking/credit_gtm";
import {CreditAlgolyticsViewType} from "../../../credit_calculator/tracking/types/CreditAlgolyticsViewType";
import {CalculatorValueType} from "../../../credit_calculator/types/CalculatorValueType";
import {calculateInstallment} from "../../../credit_calculator/utils/calculate_installment";
import {convertInterestToText} from "../../../credit_calculator/utils/convert_interest_to_text";
import {formatBigNumbers} from "../../../credit_calculator/utils/format_big_numbers";
import {getCalculatorInitialValues} from "../../../credit_calculator/utils/get_calculator_initial_values";
import {redirectToCreditOffers} from "../../../credit_calculator/utils/redirect_to_credit_offers";
import {getOfferTypeName, getOfferTypeNameWhich} from "../../../offer/helpers/OfferType";
import {useRegionAveragePriceByRooms} from "../../../region/hooks/use_region_average_prices";
import {trackAmplitudeButtonClick} from "../../../tracking/amplitude/track_amplitude_button_click";
import {getCurrency} from "../../../utils/get_currency";
import {PropertyCreditCalculatorRangesOptions} from "../../constants/property_credit_calculator_ranges_options";
import {IPropertyForSale} from "../../interfaces/IPropertyForSale";

interface IProps {
    property: IPropertyForSale;
    currency: number;
    onInteraction?: () => void;
}

export const PropertyCreditCalculator = (props: IProps) => {
    const sendEventsRef = useRef<AllCreditGtmEventActions[]>([]);

    const {contribution, period, updateValue} = useCalculatorState(getCalculatorInitialValues(PropertyCreditCalculatorRangesOptions));

    const iconProps = {
        size: "1.6",
        wrapperSize: "1.6",
        wrapperColor: "transparent"
    };

    const {regionAveragePriceByRooms, regionAverageMinByRooms, regionAverageMaxByRooms} = useRegionAveragePriceByRooms({
        region: props.property.offer.region,
        offerType: props.property.offer.type,
        roomsCount: props.property.rooms
    });

    const price = useMemo(
        () => (props.property.configuration.show_price && props.property.price ? Math.round(props.property.price) : Math.round(regionAveragePriceByRooms)),
        [regionAveragePriceByRooms, props.property.price]
    );
    const calculatedContribution = useMemo(() => Math.round((contribution * price) / 100), [price, contribution]);
    const installment = calculateInstallment(price, contribution, period);
    const buttonTitle = `zapytaj o ${getOfferTypeName(props.property.offer.type)} z kredytem`;

    const hitCreditValuesEvent = useCallback(() => {
        CreditValuesEvent({
            credit_period: period,
            own_contribution_amount: calculatedContribution,
            own_contribution_percent: Math.floor(contribution),
            property_price: price,
            view_type: CreditAlgolyticsViewType.PROPERTY
        });
    }, [price, contribution, period, calculatedContribution]);

    useTimeout(
        () => {
            if (sendEventsRef.current.length > 0) {
                hitCreditValuesEvent();
            }
        },
        5000,
        [price, contribution, period]
    );

    useEffect(() => {
        if (price) {
            creditCalculatorAnalytics.gtm.creditCalculatorEvent(CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR, {
                credit_action: CreditCalculatorViewActions.OFFER_VIEW
            });
        }
    }, [price]);

    const getValueFromType = (type: CalculatorValueType) => {
        switch (type) {
            case CalculatorValueType.PERIOD:
                return period;
            case CalculatorValueType.CONTRIBUTION:
                return contribution;
            default:
                return 0;
        }
    };

    const onValueChange = (type: CalculatorValueType, value: number) => {
        props.onInteraction?.();

        if (type === CalculatorValueType.PERIOD && !sendEventsRef.current.includes(CreditCalculatorInstalmentEventAction.PERIOD)) {
            creditCalculatorAnalytics.gtm.creditCalculatorEvent(CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR, {
                credit_action: CreditCalculatorInstalmentEventAction.PERIOD
            });
            sendEventsRef.current = [...sendEventsRef.current, CreditCalculatorInstalmentEventAction.PERIOD];
        }

        if (type === CalculatorValueType.CONTRIBUTION && !sendEventsRef.current.includes(CreditCalculatorInstalmentEventAction.CONTRIBUTION)) {
            creditCalculatorAnalytics.gtm.creditCalculatorEvent(CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR, {
                credit_action: CreditCalculatorInstalmentEventAction.CONTRIBUTION
            });
            sendEventsRef.current = [...sendEventsRef.current, CreditCalculatorInstalmentEventAction.CONTRIBUTION];
        }

        updateValue(type, value);
    };

    const currency = getCurrency({
        currency: props.currency
    });

    const customDescription = useMemo(
        () => (
            <Text variant="info_txt_1" align="center" css={mt(2)}>
                {`Kalkulacja raty kredytowej została oparta o estymowaną cenę nieruchomości (${
                    props.property.price
                        ? `${formatBigNumbers(price)} ${currency}`
                        : `${formatBigNumbers(Math.round(regionAverageMinByRooms))} - ${formatBigNumbers(Math.round(regionAverageMaxByRooms))} ${currency}`
                }) na podstawie ostatnich danych od dewelopera i naszych danych analitycznych`}
            </Text>
        ),
        [price, regionAverageMinByRooms, regionAverageMaxByRooms]
    );

    const onRedirect = () => {
        trackAmplitudeButtonClick({
            buttonName: "view_bank_offers",
            value: "view_bank_offers",
            buttonCategory: "property_page",
            itemId: props.property.offer.id,
            propertyId: props.property.id,
            buttonLink: rpAppLink.credits.offers()
        });
        redirectToCreditOffers(price, contribution, period);
    };

    const gtmData = {
        clickText: buttonTitle,
        source: GTMEventApplicationSourceSection.CREDIT,
        vendorName: props.property.offer.vendor.name ?? props.property.offer.vendor.id.toString(),
        offerName: props.property.offer.name ?? props.property.offer.id.toString()
    };

    return (
        <div css={[calculatorWrapper, !!price ? flex() : display("none")]}>
            <Text variant="headline_2" css={mb(0.5)}>
                Kup {getOfferTypeNameWhich(props.property.offer.type)} <Highlight>na kredyt</Highlight>
            </Text>
            <Text variant="body_copy_2">Symulacja miesięcznej raty kredytowej</Text>
            <div>
                {PropertyCreditCalculatorRangesOptions.map((el) => (
                    <div key={el.name} css={mt(3)}>
                        <RangeWithInput
                            rangeOptions={el}
                            value={getValueFromType(el.valueType)}
                            price={el.uniteType === UnitsType.PERCENTS ? price : undefined}
                            updateValue={onValueChange}
                            currency={currency}
                        />
                    </div>
                ))}

                <div css={infoWrapper}>
                    <IconWithElement icon={InfoIcon} iconProps={iconProps} containerStyle={iconWithElementStyle}>
                        <Text variant="info_txt_1">
                            Kalkulator wylicza orientacyjną ratę Twojego kredytu przy założeniu, że{" "}
                            <strong>średnie oprocentowanie kredytu wraz z WIBOR i marżą banku wynosi {convertInterestToText(DEFAULT_INTEREST)}.</strong>
                        </Text>
                    </IconWithElement>
                </div>
            </div>
            <div css={mv(3)}>
                <InstallmentBox
                    installment={installment}
                    customizedBoxWrapper={instalmentBoxWrapper}
                    hideBottomSection
                    smallTopSection
                    customDescription={customDescription}
                    currency={currency}
                />
            </div>

            <div css={buttonsHolder}>
                <ApplicationModalButton
                    source={ApplicationSource.CreditCalculator}
                    sourceSection={ApplicationSourceSection.PROPERTY_CREDIT_CALCULATOR}
                    vendorId={props.property.offer.vendor.id}
                    propertyId={props.property.id}
                    offerId={props.property.offer.id}
                    render={({openModal}) => (
                        <Button
                            variant="outlined_secondary"
                            size="small"
                            onClick={() => {
                                trackAmplitudeButtonClick({
                                    buttonName: "ask_property_with_mortgage",
                                    value: "ask_property_with_mortgage",
                                    buttonCategory: "property_page",
                                    itemId: props.property.offer.id,
                                    propertyId: props.property.id
                                });
                                openModal(gtmData);
                            }}
                            css={buttonStyle}
                        >
                            {buttonTitle}
                        </Button>
                    )}
                    applicationVariant={ApplicationFormVariant.CREDIT}
                    creditPayload={{price, contribution: calculatedContribution, period}}
                    disableUnlockChoice
                />

                <Button variant="outlined_secondary" size="small" type="button" onClick={onRedirect} css={buttonStyle}>
                    <Text variant="button_small">Zobacz oferty banków</Text>
                </Button>
            </div>
        </div>
    );
};

const calculatorWrapper = css`
    ${p(2)};
    ${borderRadius(2)};
    ${backgroundColor("#fff")};
    ${flexDirection("column")};
    ${mb(2)};

    ${onDesktop(p(3))};
`;

const infoWrapper = (theme: Theme) => css`
    ${backgroundColor(theme.colors.gray[100])};
    ${p(2)};
    ${mt(3)};
    ${borderRadius(0.5)};

    ${onDesktop(mt(2))};
`;

const iconWithElementStyle = css`
    ${flex("flex-start", "center")};
    ${flexDirection("row")};
    gap: ${calculateRemSize(0.5)};
`;

const instalmentBoxWrapper = css`
    ${borderRadius(2)};
    ${w100};
`;

const buttonsHolder = css`
    ${flex("flex-start", "center")};
    ${flexDirection("column")};
    gap: ${calculateRemSize(2)};

    ${onDesktop(css`
        ${flexDirection("row")};
    `)};
`;

const buttonStyle = css`
    width: 100%;

    ${onDesktop(css`
        width: 50%;
    `)};
`;
import React, {useCallback, useEffect, useMemo, useRef} from "react";
import {css, Theme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {backgroundColor, borderRadius, calculateRemSize, display, flex, flexDirection, mb, mt, mv, onDesktop, p, w100} from "@pg-design/helpers-css";
import {InfoIcon} from "@pg-design/icons-module";
import {UnitsType} from "@pg-design/range-module";
import {Highlight, Text} from "@pg-design/text-module";
import {useTimeout} from "@pg-mono/hooks";
import {rpAppLink} from "@pg-mono/rp-routes";

import {ApplicationModalButton} from "../../../application/components/ApplicationModalButton";
import {ApplicationFormVariant} from "../../../application/types/form/ApplicationFormVariant";
import {GTMEventApplicationSourceSection} from "../../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {IconWithElement} from "../../../credit_calculator/components/IconWithElement";
import {InstallmentBox} from "../../../credit_calculator/components/instalment_calculator/components/InstallmentBox";
import {RangeWithInput} from "../../../credit_calculator/components/RangeWithInput";
import {DEFAULT_INTEREST} from "../../../credit_calculator/constants/calculations_defaults";
import {useCalculatorState} from "../../../credit_calculator/hooks/use_calculator_state";
import {CreditValuesEvent} from "../../../credit_calculator/tracking/analytics/credit_values_event";
import {
    AllCreditGtmEventActions,
    creditCalculatorAnalytics,
    CreditCalculatorGTMEvent,
    CreditCalculatorInstalmentEventAction,
    CreditCalculatorViewActions
} from "../../../credit_calculator/tracking/credit_gtm";
import {CreditAlgolyticsViewType} from "../../../credit_calculator/tracking/types/CreditAlgolyticsViewType";
import {CalculatorValueType} from "../../../credit_calculator/types/CalculatorValueType";
import {calculateInstallment} from "../../../credit_calculator/utils/calculate_installment";
import {convertInterestToText} from "../../../credit_calculator/utils/convert_interest_to_text";
import {formatBigNumbers} from "../../../credit_calculator/utils/format_big_numbers";
import {getCalculatorInitialValues} from "../../../credit_calculator/utils/get_calculator_initial_values";
import {redirectToCreditOffers} from "../../../credit_calculator/utils/redirect_to_credit_offers";
import {getOfferTypeName, getOfferTypeNameWhich} from "../../../offer/helpers/OfferType";
import {useRegionAveragePriceByRooms} from "../../../region/hooks/use_region_average_prices";
import {trackAmplitudeButtonClick} from "../../../tracking/amplitude/track_amplitude_button_click";
import {getCurrency} from "../../../utils/get_currency";
import {PropertyCreditCalculatorRangesOptions} from "../../constants/property_credit_calculator_ranges_options";
import {IPropertyForSale} from "../../interfaces/IPropertyForSale";

interface IProps {
    property: IPropertyForSale;
    currency: number;
    onInteraction?: () => void;
}

export const PropertyCreditCalculator = (props: IProps) => {
    const sendEventsRef = useRef<AllCreditGtmEventActions[]>([]);

    const {contribution, period, updateValue} = useCalculatorState(getCalculatorInitialValues(PropertyCreditCalculatorRangesOptions));

    const iconProps = {
        size: "1.6",
        wrapperSize: "1.6",
        wrapperColor: "transparent"
    };

    const {regionAveragePriceByRooms, regionAverageMinByRooms, regionAverageMaxByRooms} = useRegionAveragePriceByRooms({
        region: props.property.offer.region,
        offerType: props.property.offer.type,
        roomsCount: props.property.rooms
    });

    const price = useMemo(
        () => (props.property.configuration.show_price && props.property.price ? Math.round(props.property.price) : Math.round(regionAveragePriceByRooms)),
        [regionAveragePriceByRooms, props.property.price]
    );
    const calculatedContribution = useMemo(() => Math.round((contribution * price) / 100), [price, contribution]);
    const installment = calculateInstallment(price, contribution, period);
    const buttonTitle = `zapytaj o ${getOfferTypeName(props.property.offer.type)} z kredytem`;

    const hitCreditValuesEvent = useCallback(() => {
        CreditValuesEvent({
            credit_period: period,
            own_contribution_amount: calculatedContribution,
            own_contribution_percent: Math.floor(contribution),
            property_price: price,
            view_type: CreditAlgolyticsViewType.PROPERTY
        });
    }, [price, contribution, period, calculatedContribution]);

    useTimeout(
        () => {
            if (sendEventsRef.current.length > 0) {
                hitCreditValuesEvent();
            }
        },
        5000,
        [price, contribution, period]
    );

    useEffect(() => {
        if (price) {
            creditCalculatorAnalytics.gtm.creditCalculatorEvent(CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR, {
                credit_action: CreditCalculatorViewActions.OFFER_VIEW
            });
        }
    }, [price]);

    const getValueFromType = (type: CalculatorValueType) => {
        switch (type) {
            case CalculatorValueType.PERIOD:
                return period;
            case CalculatorValueType.CONTRIBUTION:
                return contribution;
            default:
                return 0;
        }
    };

    const onValueChange = (type: CalculatorValueType, value: number) => {
        props.onInteraction?.();

        if (type === CalculatorValueType.PERIOD && !sendEventsRef.current.includes(CreditCalculatorInstalmentEventAction.PERIOD)) {
            creditCalculatorAnalytics.gtm.creditCalculatorEvent(CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR, {
                credit_action: CreditCalculatorInstalmentEventAction.PERIOD
            });
            sendEventsRef.current = [...sendEventsRef.current, CreditCalculatorInstalmentEventAction.PERIOD];
        }

        if (type === CalculatorValueType.CONTRIBUTION && !sendEventsRef.current.includes(CreditCalculatorInstalmentEventAction.CONTRIBUTION)) {
            creditCalculatorAnalytics.gtm.creditCalculatorEvent(CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR, {
                credit_action: CreditCalculatorInstalmentEventAction.CONTRIBUTION
            });
            sendEventsRef.current = [...sendEventsRef.current, CreditCalculatorInstalmentEventAction.CONTRIBUTION];
        }

        updateValue(type, value);
    };

    const currency = getCurrency({
        currency: props.currency
    });

    const customDescription = useMemo(
        () => (
            <Text variant="info_txt_1" align="center" css={mt(2)}>
                {`Kalkulacja raty kredytowej została oparta o estymowaną cenę nieruchomości (${
                    props.property.price
                        ? `${formatBigNumbers(price)} ${currency}`
                        : `${formatBigNumbers(Math.round(regionAverageMinByRooms))} - ${formatBigNumbers(Math.round(regionAverageMaxByRooms))} ${currency}`
                }) na podstawie ostatnich danych od dewelopera i naszych danych analitycznych`}
            </Text>
        ),
        [price, regionAverageMinByRooms, regionAverageMaxByRooms]
    );

    const onRedirect = () => {
        trackAmplitudeButtonClick({
            buttonName: "view_bank_offers",
            value: "view_bank_offers",
            buttonCategory: "property_page",
            itemId: props.property.offer.id,
            propertyId: props.property.id,
            buttonLink: rpAppLink.credits.offers()
        });
        redirectToCreditOffers(price, contribution, period);
    };

    const gtmData = {
        clickText: buttonTitle,
        source: GTMEventApplicationSourceSection.CREDIT,
        vendorName: props.property.offer.vendor.name ?? props.property.offer.vendor.id.toString(),
        offerName: props.property.offer.name ?? props.property.offer.id.toString()
    };

    return (
        <div css={[calculatorWrapper, !!price ? flex() : display("none")]}>
            <Text variant="headline_2" css={mb(0.5)}>
                Kup {getOfferTypeNameWhich(props.property.offer.type)} <Highlight>na kredyt</Highlight>
            </Text>
            <Text variant="body_copy_2">Symulacja miesięcznej raty kredytowej</Text>
            <div>
                {PropertyCreditCalculatorRangesOptions.map((el) => (
                    <div key={el.name} css={mt(3)}>
                        <RangeWithInput
                            rangeOptions={el}
                            value={getValueFromType(el.valueType)}
                            price={el.uniteType === UnitsType.PERCENTS ? price : undefined}
                            updateValue={onValueChange}
                            currency={currency}
                        />
                    </div>
                ))}

                <div css={infoWrapper}>
                    <IconWithElement icon={InfoIcon} iconProps={iconProps} containerStyle={iconWithElementStyle}>
                        <Text variant="info_txt_1">
                            Kalkulator wylicza orientacyjną ratę Twojego kredytu przy założeniu, że{" "}
                            <strong>średnie oprocentowanie kredytu wraz z WIBOR i marżą banku wynosi {convertInterestToText(DEFAULT_INTEREST)}.</strong>
                        </Text>
                    </IconWithElement>
                </div>
            </div>
            <div css={mv(3)}>
                <InstallmentBox
                    installment={installment}
                    customizedBoxWrapper={instalmentBoxWrapper}
                    hideBottomSection
                    smallTopSection
                    customDescription={customDescription}
                    currency={currency}
                />
            </div>

            <div css={buttonsHolder}>
                <ApplicationModalButton
                    source={ApplicationSource.CreditCalculator}
                    sourceSection={ApplicationSourceSection.PROPERTY_CREDIT_CALCULATOR}
                    vendorId={props.property.offer.vendor.id}
                    propertyId={props.property.id}
                    offerId={props.property.offer.id}
                    render={({openModal}) => (
                        <Button
                            variant="outlined_secondary"
                            size="small"
                            onClick={() => {
                                trackAmplitudeButtonClick({
                                    buttonName: "ask_property_with_mortgage",
                                    value: "ask_property_with_mortgage",
                                    buttonCategory: "property_page",
                                    itemId: props.property.offer.id,
                                    propertyId: props.property.id
                                });
                                openModal(gtmData);
                            }}
                            css={buttonStyle}
                        >
                            {buttonTitle}
                        </Button>
                    )}
                    applicationVariant={ApplicationFormVariant.CREDIT}
                    creditPayload={{price, contribution: calculatedContribution, period}}
                    disableUnlockChoice
                />

                <Button variant="outlined_secondary" size="small" type="button" onClick={onRedirect} css={buttonStyle}>
                    <Text variant="button_small">Zobacz oferty banków</Text>
                </Button>
            </div>
        </div>
    );
};

const calculatorWrapper = css`
    ${p(2)};
    ${borderRadius(2)};
    ${backgroundColor("#fff")};
    ${flexDirection("column")};
    ${mb(2)};

    ${onDesktop(p(3))};
`;

const infoWrapper = (theme: Theme) => css`
    ${backgroundColor(theme.colors.gray[100])};
    ${p(2)};
    ${mt(3)};
    ${borderRadius(0.5)};

    ${onDesktop(mt(2))};
`;

const iconWithElementStyle = css`
    ${flex("flex-start", "center")};
    ${flexDirection("row")};
    gap: ${calculateRemSize(0.5)};
`;

const instalmentBoxWrapper = css`
    ${borderRadius(2)};
    ${w100};
`;

const buttonsHolder = css`
    ${flex("flex-start", "center")};
    ${flexDirection("column")};
    gap: ${calculateRemSize(2)};

    ${onDesktop(css`
        ${flexDirection("row")};
    `)};
`;

const buttonStyle = css`
    width: 100%;

    ${onDesktop(css`
        width: 50%;
    `)};
`;
